import {
	CaseTypeItem,
	useLazyFullWorkplaceCaseTypeQuery,
} from '../../../graphql/graphql';
import { prepareTranslations } from '../../Base/Services/prepareTranslations.service';
import { CaseTypeDetailFormValues } from '../Components/CaseType/Detail/CaseTypeDetailForm';
import {
	CaseTypeOrganizationUnitFormDefaultValues,
	CaseTypeOrganizationUnitFormValues,
} from '../Components/CaseType/OrganizationUnit/CaseTypeOrganizationUnitForm';
import { Translation } from '../../Base/Interfaces/Translation.interface';

interface CaseTypeDetailFormDefaultValues
	extends Omit<CaseTypeDetailFormValues, 'parentCaseTypeId' | 'vatRate'> {
	parentCaseTypeId?: string;
	vatRate?: number;
}

export default function useCaseTypes() {
	const [getWorkplaceCaseType] = useLazyFullWorkplaceCaseTypeQuery();
	const getDefaultsValuesForCaseType = async (id: string) => {
		const { workplaceCaseType } = await getWorkplaceCaseType({
			id,
		}).unwrap();

		if (workplaceCaseType) {
			const canBook =
				workplaceCaseType?.caseType?.canBookOnline ||
				workplaceCaseType?.caseType?.canBookOnSite
					? '1'
					: '0';

			const isWithDoc = workplaceCaseType?.caseType?.caseTypeItems?.find(
				(item: any) => item?.key === 'isWithDoc',
			);
			const isWithAnswer = workplaceCaseType?.caseType?.caseTypeItems?.find(
				(item: any) => item?.key === 'isWithAnswer',
			);
			const isWithTextInformation =
				workplaceCaseType?.caseType?.caseTypeItems?.find(
					(item: any) => item?.key === 'isWithNote',
				);

			const defaultValues: CaseTypeDetailFormDefaultValues = {
				caseTypeId: workplaceCaseType?.caseType?.id ?? undefined,
				parentCaseTypeId:
					workplaceCaseType?.caseType?.parentCaseTypeId ?? undefined,
				label: workplaceCaseType?.caseType?.label ?? '',
				labelTranslations: prepareTranslations(
					workplaceCaseType?.labelTranslations as Translation[],
				),
				canOrderImmediate: workplaceCaseType?.caseType?.canOrderImmediate
					? '1'
					: '0',
				canBook: canBook,
				canBookOnline: workplaceCaseType?.caseType?.canBookOnline ?? false,
				canBookOnSite: workplaceCaseType?.caseType?.canBookOnSite ?? false,
				eventInterval:
					workplaceCaseType?.caseType?.eventInterval?.toString() ?? '',
				isPayed: workplaceCaseType?.caseType?.priceId ? '1' : '0',
				price:
					removeZerosAfterDot(
						workplaceCaseType?.caseType?.price?.priceWithVatRaw,
					) ?? '',
				vatRate: workplaceCaseType?.caseType?.price?.vatRate,
				isWithDoc: isWithDoc ? '1' : '0',
				docId: isWithDoc?.id ?? undefined,
				docName: isWithDoc?.title ?? '',
				docRequired: isWithDoc?.settings?.required ? '1' : '0',
				isWithAnswer: isWithAnswer ? '1' : '0',
				answerId: isWithAnswer?.id ?? undefined,
				answerName: isWithAnswer?.title ?? '',
				answerRequired: isWithAnswer?.settings?.required ? '1' : '0',
				isWithTextInformation: isWithTextInformation ? '1' : '0',
				textInformationId: isWithTextInformation?.id ?? undefined,
				textInformation: isWithTextInformation?.title ?? '',
				notificationCase: workplaceCaseType?.caseType?.notificationCase
					? '1'
					: '0',
				notificationBook: workplaceCaseType?.caseType?.notificationBook
					? '1'
					: '0',
				setupForMonitoringCenter: workplaceCaseType?.caseType?.code ? '1' : '0',
				code: workplaceCaseType?.caseType?.code ?? undefined,
				isEditable: workplaceCaseType?.caseType?.isEditable ?? undefined,
				hasModule:
					!!workplaceCaseType?.caseType?.module &&
					workplaceCaseType?.caseType?.module !== 'workplace',
				eventCancelWithPayment:
					workplaceCaseType?.caseType?.eventCancelWithPayment || false,
				backgroundColor:
					workplaceCaseType?.caseType?.backgroundColor ?? undefined,
			};

			return defaultValues;
		}

		return null;
	};

	const getDefaultsValuesForOrganizationUnit = async (id: string) => {
		const { workplaceCaseType } = await getWorkplaceCaseType({
			id,
		}).unwrap();

		if (workplaceCaseType) {
			const defaultValues: CaseTypeOrganizationUnitFormDefaultValues = {
				parentCaseTypeId:
					workplaceCaseType?.caseType?.parentCaseTypeId ?? undefined,
				label: workplaceCaseType?.caseType?.label ?? '',
			};

			return defaultValues;
		}

		return null;
	};

	const removeZerosAfterDot = (value: string | undefined | null) => {
		if (value) {
			return parseFloat(value).toString();
		}
		return undefined;
	};

	return { getDefaultsValuesForCaseType, getDefaultsValuesForOrganizationUnit };
}
